import './styles.css'

import { useEffect, useRef, useState } from 'react'

import { Box, Input, Text, useDisclosure } from '@chakra-ui/react'
import { Tooltip } from '@editor/components/ui/tooltip'

import { AutoCompleteItem } from './definitions'

interface AutoCompleteProps {
  defaultValue?: string
  items: AutoCompleteItem[]
  autoFocus?: boolean
  validate?: (value: string) => string | boolean
  onChange?: (item: AutoCompleteItem) => void
  onCancel?: () => void
}

const AutoComplete = ({ defaultValue, items, autoFocus, validate, onChange, onCancel }: AutoCompleteProps) => {
  const { open, onOpen, onClose } = useDisclosure()

  // Refs
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // States
  const [value, setValue] = useState<string>(defaultValue || '')
  const [error, setError] = useState<string | null>(null)

  const handleItemClick = (e: React.MouseEvent<HTMLDivElement>, item: AutoCompleteItem) => {
    if (!inputRef.current) return

    e.stopPropagation()

    if (validate) {
      const result = validate(item.name)
      if (typeof result === 'string') {
        setError(result)
        return
      }
    }

    setValue(item.name)
    inputRef.current.value = item.name
    if (onChange) onChange(item)
    onClose()
  }

  // Replace useOutsideClick with custom implementation
  useEffect(() => {
    if (!open) return undefined

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (validate) {
          const result = validate(value)
          if (typeof result === 'string') {
            setError(result)
            return
          }
        }

        if (onChange) {
          onChange({
            id: crypto.randomUUID(),
            name: value,
          })
        }

        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, value, validate, onChange, onClose])

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus()
    }
  }, [inputRef, autoFocus])

  return (
    <Box className={['auto-complete', open ? 'open' : ''].join(' ')} ref={containerRef}>
      <Tooltip
        content={<Text color="orange">{error}</Text>}
        showArrow
        open={!!error}
        positioning={{
          placement: 'top',
        }}
      >
        <Input
          ref={inputRef}
          // Stable hook for external focus targeting (e.g. InspectionGroup's
          // dialog finalFocusEl) — keep it if this component is restructured.
          data-autocomplete-input
          defaultValue={defaultValue}
          size="xs"
          placeholder="グループ名"
          onFocus={(e) => {
            e.stopPropagation()
            onOpen()
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            setError(null)

            if (e.key === 'Enter') {
              e.stopPropagation()
              if (validate) {
                const result = validate(e.currentTarget.value)
                if (typeof result === 'string') {
                  setError(result)
                  return
                }
              }

              if (onChange) {
                onChange({
                  id: crypto.randomUUID(),
                  name: e.currentTarget.value,
                })
              }
              onClose()
            } else if (e.key === 'Escape') {
              e.stopPropagation()
              if (onCancel) onCancel()
              onClose()
            }
          }}
          onInput={(e: React.FormEvent<HTMLInputElement>) => {
            onOpen()
            setValue(e.currentTarget.value)
          }}
        />
      </Tooltip>
      <Box>
        {items
          .filter((item) => item.name.toLowerCase().includes(value.toLowerCase()))
          .map((item) => (
            <Box key={item.id} onClick={(e) => handleItemClick(e, item)}>
              {item.name}
            </Box>
          ))}
      </Box>
    </Box>
  )
}

export default AutoComplete
